import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, RefreshCw, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useAuth, can } from "@/lib/auth";

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function Forecast() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    from_date: todayISO(),
    to_date: plusDaysISO(90),
    include_overdue: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/forecast", {
        params: {
          from_date: filters.from_date,
          to_date: filters.to_date,
          include_overdue: filters.include_overdue,
        },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar previsão");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (can(user, "view_financial")) load(); /* eslint-disable-next-line */ }, []);

  if (user && !can(user, "view_financial")) return <Navigate to="/" replace />;

  const isPositive = data?.prediction === "positiva";

  const presets = [
    { label: "Próx. 7 dias", from: todayISO(), to: plusDaysISO(7) },
    { label: "Próx. 30 dias", from: todayISO(), to: plusDaysISO(30) },
    { label: "Próx. 90 dias", from: todayISO(), to: plusDaysISO(90) },
    { label: "Próx. 12 meses", from: todayISO(), to: plusDaysISO(365) },
  ];

  return (
    <Layout title="Previsão de Entradas e Saídas" subtitle="Projeção com base em contas a receber e contas a pagar futuras">
      {/* Filters */}
      <Card className="rounded-sm p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs font-semibold">De</Label>
            <Input data-testid="forecast-from" type="date" className="mt-1 w-44" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs font-semibold">Até</Label>
            <Input data-testid="forecast-to" type="date" className="mt-1 w-44" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} />
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Switch
              data-testid="forecast-include-overdue"
              checked={filters.include_overdue}
              onCheckedChange={(v) => setFilters({ ...filters, include_overdue: !!v })}
            />
            <Label className="text-xs">Incluir vencidos</Label>
          </div>
          <Button data-testid="forecast-apply" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Aplicar filtros
          </Button>
          <div className="flex flex-wrap items-center gap-1 ml-auto">
            {presets.map(p => (
              <Button key={p.label} variant="ghost" size="sm" onClick={() => { setFilters(f => ({ ...f, from_date: p.from, to_date: p.to })); }}>{p.label}</Button>
            ))}
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Entradas previstas</p>
          <p className="kpi-num text-2xl mt-2 text-emerald-700" data-testid="forecast-kpi-entradas">{fmtBRL(data?.totals?.entradas || 0)}</p>
        </Card>
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Saídas previstas</p>
          <p className="kpi-num text-2xl mt-2 text-red-700" data-testid="forecast-kpi-saidas">{fmtBRL(data?.totals?.saidas || 0)}</p>
        </Card>
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Saldo previsto</p>
          <p className={`kpi-num text-2xl mt-2 ${isPositive ? "text-emerald-700" : "text-red-700"}`} data-testid="forecast-kpi-saldo">{fmtBRL(data?.totals?.saldo || 0)}</p>
        </Card>
        <Card className={`p-4 rounded-sm border-2 ${isPositive ? "border-emerald-300 bg-emerald-50/40" : "border-red-300 bg-red-50/40"}`}>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Resultado</p>
          <div className="flex items-center gap-2 mt-2">
            {isPositive ? <TrendingUp className="w-7 h-7 text-emerald-700" /> : <TrendingDown className="w-7 h-7 text-red-700" />}
            <p className={`kpi-num text-2xl ${isPositive ? "text-emerald-700" : "text-red-700"}`} data-testid="forecast-prediction">
              {isPositive ? "Previsão Positiva" : "Previsão Negativa"}
            </p>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="daily">
        <TabsList>
          <TabsTrigger value="daily" data-testid="forecast-tab-daily">Por dia</TabsTrigger>
          <TabsTrigger value="items" data-testid="forecast-tab-items">Detalhamento</TabsTrigger>
        </TabsList>

        <TabsContent value="daily">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60">
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Entradas</TableHead>
                  <TableHead className="text-right">Saídas</TableHead>
                  <TableHead className="text-right">Saldo do dia</TableHead>
                  <TableHead className="text-right">Saldo acumulado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.daily || []).map((d, i) => (
                  <TableRow key={i} data-testid={`forecast-daily-row-${i}`}>
                    <TableCell className="font-medium">{fmtDate(d.data)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{fmtBRL(d.entradas)}</TableCell>
                    <TableCell className="text-right text-red-700">{fmtBRL(d.saidas)}</TableCell>
                    <TableCell className={`text-right ${d.saldo_dia >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtBRL(d.saldo_dia)}</TableCell>
                    <TableCell className={`text-right font-semibold ${d.saldo_acumulado >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtBRL(d.saldo_acumulado)}</TableCell>
                  </TableRow>
                ))}
                {!data?.daily?.length && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sem lançamentos previstos no período selecionado.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="items">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60">
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items || []).map((it, i) => (
                  <TableRow key={i} data-testid={`forecast-item-row-${i}`}>
                    <TableCell>{fmtDate(it.data)}</TableCell>
                    <TableCell>
                      {it.tipo === "entrada"
                        ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><ArrowUpRight className="w-3 h-3"/>Entrada</span>
                        : <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold"><ArrowDownRight className="w-3 h-3"/>Saída</span>}
                    </TableCell>
                    <TableCell className="text-xs">{it.descricao}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-[10px]">{it.status}</Badge></TableCell>
                    <TableCell className={`text-right ${it.tipo === "entrada" ? "text-emerald-700" : "text-red-700"}`}>{fmtBRL(it.valor)}</TableCell>
                  </TableRow>
                ))}
                {!data?.items?.length && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sem lançamentos previstos no período selecionado.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
